import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store';
import {
  getLeaveApprovals,
  getLeaveTypes,
  LeaveTypeResponse,
  updateLeaveApprovals,
  AttachmentDTO
} from 'store/Leaves';
import './LeaveRequest.scss';
import { useNavigate } from 'react-router';
import { Typography, ButtonPro, TextAreaProControlled } from 'components';
import {
  Model,
  ModalBody,
  ModalHeader
} from 'components/atoms';
import { LeaveStatus, LeaveApprovalType } from 'models/enums';
import Chip from '@mui/material/Chip';
import { toast } from 'react-toastify';
import { toDotNetTicks } from 'utils/dateTicks';

// Holiday interface based on the structure you provided
interface Holiday {
  date: string;
  description: string;
  applicability: string;
  holidaySession: string;
  event: string;
}

const LeaveRequests = () => {
  const [comments, setComments] = useState<{[key: string]: string}>({});
  const [rejectModal, setRejectModal] = useState<{
    approvalId: string;
    employeeName: string;
    isCancellation: boolean;
  } | null>(null);
  const [rejectComment, setRejectComment] = useState('');
  const [isRejectSubmitting, setIsRejectSubmitting] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { leaveApprovals, leaveTypeInfo, holidays } = useAppSelector(
    (state) => state.leaves
  );

  const { upn } = useAppSelector((state) => state.auth.user.authData);
  const userRole = useAppSelector(
    (state) => state.auth.user.authData?.data?.userRole
  );
  useEffect(() => {
    dispatch(getLeaveTypes());
    dispatch(getLeaveApprovals(upn));
  }, [dispatch, upn]);

  function findLeaveType(leaveTypeId: string) {
    return leaveTypeInfo?.find(
      (leaveType: LeaveTypeResponse) => leaveType.leaveTypeID === leaveTypeId
    );
  }

  // Function to generate 2-week calendar view for leave request
  const generateCalendarView = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const calendarDates = [];

    // Find the Monday of the week containing start date for 2-week view
    const firstMonday = new Date(start);
    firstMonday.setDate(start.getDate() - start.getDay() + 1);

    // Generate 14 days (2 weeks)
    for (let i = 0; i < 14; i++) {
      const currentDate = new Date(firstMonday);
      currentDate.setDate(firstMonday.getDate() + i);
      calendarDates.push(currentDate);
    }

    return calendarDates;
  };

  const formatMonthYear = (date: Date) =>
    date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const getCalendarPeriodLabel = (startDate: string, endDate: string) => {
    const dates = generateCalendarView(startDate, endDate);
    if (dates.length === 0) return '';

    const firstLabel = formatMonthYear(dates[0]);
    const lastLabel = formatMonthYear(dates[dates.length - 1]);

    return firstLabel === lastLabel ? firstLabel : `${firstLabel} – ${lastLabel}`;
  };

  // Function to check if date is a holiday
  const isHoliday = (date: Date) => {
    const dateStr =
      date.getFullYear() +
      '-' +
      String(date.getMonth() + 1).padStart(2, '0') +
      '-' +
      String(date.getDate()).padStart(2, '0');
    return holidays?.some((holiday: Holiday) => {
      const holidayDate = holiday.date.split('T')[0];
      return holidayDate === dateStr;
    });
  };

  // Function to check if date is weekend
  const isWeekend = (date: Date) => {
    const day = date.getDay();
    return day === 0 || day === 6; // Sunday = 0, Saturday = 6
  };

  // Function to check if date is in leave range
  const isLeaveDate = (date: Date, startDate: string, endDate: string) => {
    const dateStr =
      date.getFullYear() +
      '-' +
      String(date.getMonth() + 1).padStart(2, '0') +
      '-' +
      String(date.getDate()).padStart(2, '0');
    const start = startDate.split('T')[0];
    const end = endDate.split('T')[0];
    return dateStr >= start && dateStr <= end;
  };

  // Function to get holiday details for a specific date
  const getHolidayDetails = (date: Date) => {
    const dateStr =
      date.getFullYear() +
      '-' +
      String(date.getMonth() + 1).padStart(2, '0') +
      '-' +
      String(date.getDate()).padStart(2, '0');
    return holidays?.find((holiday: Holiday) => {
      const holidayDate = holiday.date.split('T')[0];
      return holidayDate === dateStr;
    });
  };

  const handleApproval = async (
    leaveApprovalId: string,
    status: number,
    isCancellation: boolean
  ) => {
    const comment = comments[leaveApprovalId] || '';

    try {
      await dispatch(
        updateLeaveApprovals({
          id: leaveApprovalId,
          status,
          comments: comment,
          isCancellation,
          userRole
        })
      ).unwrap();

      dispatch(getLeaveApprovals(upn));

      setComments(prev => ({
        ...prev,
        [leaveApprovalId]: ''
      }));
    } catch (error) {
      console.error('Failed to update leave approval:', error);
    }
  };

  const openRejectModal = (
    approvalId: string,
    employeeName: string,
    isCancellation: boolean
  ) => {
    setRejectComment(comments[approvalId] || '');
    setRejectModal({ approvalId, employeeName, isCancellation });
  };

  const closeRejectModal = () => {
    setRejectModal(null);
    setRejectComment('');
    setIsRejectSubmitting(false);
  };

  const confirmReject = async () => {
    if (!rejectModal) return;

    setIsRejectSubmitting(true);
    try {
      await dispatch(
        updateLeaveApprovals({
          id: rejectModal.approvalId,
          status: LeaveStatus.REJECTED,
          comments: rejectComment.trim(),
          isCancellation: rejectModal.isCancellation,
          userRole
        })
      ).unwrap();

      dispatch(getLeaveApprovals(upn));
      setComments((prev) => ({
        ...prev,
        [rejectModal.approvalId]: ''
      }));
      closeRejectModal();
    } catch (error) {
      console.error('Failed to reject leave approval:', error);
      setIsRejectSubmitting(false);
    }
  };

  const openExistingFile = (url: string, fileName: string) => {
    try {
      if (!url || url.trim() === '') {
        toast.error('Unable to open attachment: Invalid URL');
        console.error('Invalid attachment URL:', url);
        return;
      }

      console.log('Opening attachment:', fileName, 'URL:', url);
      const newWindow = window.open(url, '_blank');

      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        toast.error('Pop-up blocked. Please allow pop-ups for this site to view attachments.');
      }
    } catch (error) {
      console.error('Error opening attachment:', error);
      toast.error('Failed to open attachment. Please try again.');
    }
  };

  return (
    <div className="leave-requests-container">
      <div className="leave-requests-page-header">
        <div className="page-header-top">
          <ButtonPro
            label="Back to Dashboard"
            type="white"
            size="base"
            onClick={() => navigate('/')}
            icon="ri-arrow-left-line"
            buttonClassNames="leave-request-btn back"
          />
        </div>
        <div className="page-header-content">
          <div className="page-title-section">
            <Typography
              text="Leave Approval Requests"
              type="base_regular"
              weight="bold"
              className="page-main-title"
            />
            <Typography
              text={
                (leaveApprovals?.length ?? 0) > 0
                  ? `${leaveApprovals.length} Leave requests are pending your approval. Please review and take the necessary action.`
                  : 'No leave requests pending approval.'
              }
              type="small_regular"
              className="page-subtitle"
            />
          </div>
          <ButtonPro
            label="View Past Approvals"
            type="primary"
            size="base"
            buttonClassNames="leave-request-btn past"
            // onClick={() => navigate('/leave-approvals/past')}
          />
        </div>
      </div>
      {(leaveApprovals?.length ?? 0) > 0 ? (
        <div className="leave-requests-list">
          {leaveApprovals.map((item) => {
            const isCancellation =
              item.leaveApproval.approvalType === LeaveApprovalType.CANCELLATION;

            return (
            <div className="leave-request-card" key={item.leaveApproval.id}>
              <div className="leave-request-header">
                <div>
                  <span className="leave-request-type">
                    {isCancellation ? 'Cancellation: ' : ''}
                    {findLeaveType(item.leaveDetail.leaveTypeId)?.leaveType}
                  </span>
                  <span
                    className={`leave-request-status status-${item.leaveApproval.status}`}
                  >
                    {item.leaveApproval.status === LeaveStatus.PENDING
                      ? isCancellation
                        ? 'Cancellation Pending'
                        : 'Pending'
                      : item.leaveApproval.status === LeaveStatus.APPROVED
                      ? 'Approved'
                      : 'Rejected'}
                  </span>
                </div>
                <div className="leave-request-actions">
                  <ButtonPro
                    label="View details"
                    type="white"
                    size="small"
                    onClick={() =>
                      navigate(
                        `/respondLeaveRequest?leaveId=${item.leaveDetail.detailsId}&updated=${toDotNetTicks(item.leaveDetail.updated)}`
                      )
                    }
                    buttonClassNames="leave-request-btn details"
                  />
                  <ButtonPro
                    label={isCancellation ? 'Approve Cancellation' : 'Approve'}
                    type="primary"
                    size="small"
                    onClick={() =>
                      handleApproval(
                        item.leaveApproval.id,
                        LeaveStatus.APPROVED,
                        isCancellation
                      )
                    }
                    buttonClassNames="leave-request-btn approve"
                  />
                  <ButtonPro
                    label={isCancellation ? 'Reject Cancellation' : 'Reject'}
                    type="red"
                    size="small"
                    onClick={() =>
                      openRejectModal(
                        item.leaveApproval.id,
                        item.requestedPersonNickName,
                        isCancellation
                      )
                    }
                    buttonClassNames="leave-request-btn reject"
                  />
                </div>
              </div>
              <div className="leave-request-body">
                <div className="leave-request-body-grid">
                  <div className="leave-request-body">
                    <div className="leave-request-label">
                      <strong>Employee Name:</strong>{' '}
                      <p>{item.requestedPersonNickName}</p>
                    </div>
                    <div className="leave-request-label">
                      <strong>Leave Start:</strong>{' '}
                      <p>{item.leaveDetail.startDate.slice(0, 10)}</p>
                    </div>
                    <div className="leave-request-label">
                      <strong>Leave End:</strong>{' '}
                      <p>{item.leaveDetail.endDate.slice(0, 10)}</p>
                    </div>
                    <div className="leave-request-label">
                      <strong>Leave Type:</strong>{' '}
                      <p>
                        {findLeaveType(item.leaveDetail.leaveTypeId)
                          ?.leaveType || 'N/A'}
                      </p>
                    </div>
                    <div className="leave-request-label">
                      <strong>No of Days:</strong>{' '}
                      <p>{item.leaveDetail.noOfDays}</p>
                    </div>
                    <div className="leave-request-label">
                      <strong>Description:</strong>{' '}
                      <p>{item.leaveDetail.reason}</p>
                    </div>
                    {isCancellation && item.leaveApproval.cancellationReason && (
                      <div className="leave-request-label">
                        <strong>Cancellation Reason:</strong>{' '}
                        <p>{item.leaveApproval.cancellationReason}</p>
                      </div>
                    )}

                    {/* Attachments Display */}
                    {Array.isArray(item.attachments) &&
                      item.attachments.length > 0 && (
                        <div className="leave-request-label attachments-section">
                          <strong>Attachments:</strong>
                          <div className="attachments-list" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                            {item.attachments.map(
                              (attachment: AttachmentDTO, index: number) => {
                                const fileName = attachment.name || `Attachment ${index + 1}`;
                                const blobUrl = process.env.REACT_APP_BLOB_STORAGE_URL || '';
                                const fullUrl = blobUrl + fileName;

                                return (
                                  <Chip
                                    key={attachment.id || index}
                                    label={fileName}
                                    onClick={() => openExistingFile(fullUrl, fileName)}
                                    clickable
                                    variant="outlined"
                                    color="primary"
                                    icon={<i className="ri-file-download-line"></i>}
                                  />
                                );
                              }
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                  {/* Calendar View for Leave Period */}
                  <div className="leave-request-calendar-section">
                    <strong>Leave Period Calendar:</strong>
                    <div className="calendar-period-label">
                      {getCalendarPeriodLabel(
                        item.leaveDetail.startDate,
                        item.leaveDetail.endDate
                      )}
                    </div>
                    <div className="calendar-container">
                      <div className="calendar-grid">
                        {/* Calendar Headers */}
                        <div className="calendar-header">
                          <span>Mon</span>
                          <span>Tue</span>
                          <span>Wed</span>
                          <span>Thu</span>
                          <span>Fri</span>
                          <span>Sat</span>
                          <span>Sun</span>
                        </div>
                        {/* Calendar Days */}
                        <div className="calendar-days">
                          {generateCalendarView(
                            item.leaveDetail.startDate,
                            item.leaveDetail.endDate
                          ).map((date, index) => {
                            const isLeave = isLeaveDate(
                              date,
                              item.leaveDetail.startDate,
                              item.leaveDetail.endDate
                            );
                            const isWeekendDay = isWeekend(date);
                            const isHolidayDay = isHoliday(date);
                            const holidayInfo = getHolidayDetails(date);

                            return (
                              <div
                                key={index}
                                className={`calendar-day ${
                                  isLeave ? 'leave-day' : ''
                                } ${isWeekendDay ? 'weekend' : ''} ${
                                  isHolidayDay ? 'holiday' : ''
                                }`}
                                title={
                                  isHolidayDay
                                    ? `${
                                        holidayInfo?.description || 'Holiday'
                                      } - ${holidayInfo?.event || ''}`
                                    : isLeave
                                    ? 'Leave Day'
                                    : isWeekendDay
                                    ? 'Weekend'
                                    : ''
                                }
                              >
                                <span className="day-number">
                                  {date.getDate()}
                                </span>
                                {isHolidayDay && (
                                  <span className="holiday-indicator">H</span>
                                )}
                                {isLeave && (
                                  <span className="leave-indicator">L</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Comment input for approver */}
                <div className="leave-request-comment-row leave-request-label">
                  <TextAreaProControlled
                    label="Comment (optional)"
                    name="approverComment"
                    placeholder="Add a comment for approval..."
                    value={comments[item.leaveApproval.id] || ''}
                    onChange={(value) => setComments(prev => ({
                      ...prev,
                      [item.leaveApproval.id]: value
                    }))}
                    inputWrapperClass="comment-wrapper"
                    inputClass="leave-request-comment-box"
                  />
                </div>

                {item.leaveApproval.comments && (
                  <div>
                    <strong>Comments:</strong> {item.leaveApproval.comments}
                  </div>
                )}
              </div>
            </div>
          );
          })}
        </div>
      ) : (
        <div className="leave-requests-empty">No leave approvals found.</div>
      )}

      <Model
        modalSize="sm"
        showModal={Boolean(rejectModal)}
        onClose={closeRejectModal}
      >
        <ModalHeader
          headerTitle={
            rejectModal?.isCancellation
              ? 'Reject Leave Cancellation'
              : 'Reject Leave Request'
          }
          onClose={closeRejectModal}
        />
        <ModalBody>
          <div className="reject-leave-modal">
            <Typography
              type="small_regular"
              text={
                rejectModal
                  ? rejectModal.isCancellation
                    ? `Are you sure you want to reject ${rejectModal.employeeName}'s cancellation request? The leave will remain approved.`
                    : `Are you sure you want to reject ${rejectModal.employeeName}'s leave request?`
                  : ''
              }
            />
            <TextAreaProControlled
              label="Rejection comment (optional)"
              name="rejectComment"
              placeholder="Add a reason for rejection..."
              value={rejectComment}
              onChange={setRejectComment}
              inputWrapperClass="comment-wrapper"
              inputClass="leave-request-comment-box"
            />
            <div className="reject-leave-modal-actions">
              <ButtonPro
                label="Cancel"
                type="white"
                size="base"
                onClick={closeRejectModal}
                disabled={isRejectSubmitting}
              />
              <ButtonPro
                label={isRejectSubmitting ? 'Rejecting...' : 'Confirm Reject'}
                type="red"
                size="base"
                onClick={confirmReject}
                disabled={isRejectSubmitting}
                buttonClassNames="leave-request-btn reject"
              />
            </div>
          </div>
        </ModalBody>
      </Model>
    </div>
  );
};

export default LeaveRequests;
